import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MDataTable from '~/components/PF2MDataTable';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import {
  getEquipmentTypes,
  getLoads,
  setLoadsValues,
  updateLoads,
  resetLoads,
} from '~/store/manager/actions';
import { convertTimeStampToMoment } from '~/utils/moment';

const createAllLookups = (loads) => {
  const equipGroups = new Map();
  const materials = new Map();
  const operationTypes = new Map();

  loads.forEach((load) => {
    if (!equipGroups.has(load.id_equip_group)) {
      equipGroups.set(load.id_equip_group, {
        id: load.id_equip_group,
        name: load.equip_group_name,
      });
    }

    if (!materials.has(load.id_material)) {
      materials.set(load.id_material, {
        id: load.id_material,
        name: load.material_name,
      });
    }

    if (!operationTypes.has(load.operation_type_id)) {
      operationTypes.set(load.operation_type_id, {
        id: load.operation_type_id,
        name: load.operation_type_name,
      });
    }
  });

  return {
    equipGroupLookup: Array.from(equipGroups.values()),
    materialLookup: Array.from(materials.values()),
    operationTypeLookup: Array.from(operationTypes.values()),
  };
};


const LoadTab = ({
  classes,
  loads,
  getLoads: getAll,
  getEquipmentTypes: loadEquipmentTypes,
  equipmentTypes,
  setLoadsValues: updateItem,
  updateLoads: updateEditedLoad,
  resetLoads: reset,
}) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [equipmentType, setEquipmentType] = useState(0);
  const { t: translate } = useTranslation();

  /**
   * ID 1 = Escavadeira
   * ID 2 = Caminhão
   * ID 15 = Pá Carregadeira
   * ID 29 = Rock Fill
   */
  const transportAndLoadEquips = [1, 2, 15, 29];

  useEffect(() => {
    async function fetchData() {
      await loadEquipmentTypes();
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [
    dataLoaded,
    loadEquipmentTypes,
  ]);

  useEffect(() => {
    async function fetchData() {
      await getAll(equipmentType);
    }
    if (equipmentType > 0) {
      fetchData();
    }
  }, [getAll, equipmentType]);

  const { equipGroupLookup, materialLookup, operationTypeLookup } = createAllLookups(loads);


  const statusLookup = [
    { id: 0, name: translate('common:Standard') },
    { id: 1, name: translate('common:Updated') },
  ];

  const columns = [
    {
      editable: 'never',
      title: translate('common:EquipmentGroup'),
      field: 'id_equip_group',
      lookup: equipGroupLookup,
    },
    {
      editable: 'never',
      field: 'equip_name',
      title: translate('common:Equipment'),
    },
    {
      editable: 'never',
      title: translate('common:Material'),
      field: 'id_material',
      lookup: materialLookup,
    },
    {
      editable: 'never',
      title: translate('common:OperationType'),
      field: 'operation_type_id',
      lookup: operationTypeLookup,
    },
    {
      editable: 'always',
      title: translate('common:AverageLoad'),
      field: 'value',
      required: true,
      validate: ({ value, ..._rowData }) => {
        const valueRegex = /^-?\d{1,6}(?:\.\d{1,4})?$/;

        if (!valueRegex.test(value)) {
          return { isValid: false, helperText: translate('validation:mustBeNumeric') };
        }

        return { isValid: true, helperText: '' };
      },
    },
    {
      editable: 'never',
      title: translate('common:Status'),
      field: 'status',
      lookup: equipmentType ? statusLookup : [],
      render: rowData => (rowData.status === 1 ? translate('common:Updated') : translate('common:Standard')),
    },
    {
      editable: 'never',
      title: translate('common:LastTimestampUpdate'),
      field: 'last_update_timestamp',
      render: (rowData) => {
        if (rowData.last_update_timestamp !== '') {
          const date = convertTimeStampToMoment(rowData.last_update_timestamp);
          return date.format('DD/MM/YYYY HH:mm:ss');
        }

        return '';
      },
    },
  ];

  const renderItems = (items = []) => [{ value: 0, label: translate('common:Select') }]
    .concat(...items
      .map(e => ({ value: e.id, label: e.name })));

  return (
    <div className={classes.tabContainer}>
      <div style={{
        position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%',
      }}
      >
        <span className={classes.labelTypeSelector}>
          {`${translate('common:SelectEquipmentType')}:`}
        </span>
        <PF2MSearchSelectOutlined
          value={equipmentType}
          onChange={(e) => {
            setEquipmentType(e.target.value);
          }}
          className={classes.typeSelector}
        >
          {renderItems(equipmentTypes.filter(et => transportAndLoadEquips.includes(et.id)))}
        </PF2MSearchSelectOutlined>
      </div>

      <PF2MDataTable
        title={translate('common:AverageLoad')}
        options={{
          showTitle: true,
          search: false,
          selection: true,
          filtering: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          hideDelete: true,
          hideAdd: true,
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
          columnId: 'id_link',
        }}
        onChange={(newData) => {
          newData.forEach((row) => {
            updateItem(row);
          });
        }}
        onDiscard={() => reset()}
        onApply={() => updateEditedLoad(loads, equipmentType)}
        columns={columns}
        data={loads?.length > 0 ? loads : []}
      />
    </div>
  );
};

LoadTab.propTypes = {
  classes: PropTypes.object.isRequired,
  loads: PropTypes.array,
  equipmentTypes: PropTypes.array,
  getEquipmentTypes: PropTypes.func,
  getLoads: PropTypes.func,
  setLoadsValues: PropTypes.func,
  updateLoads: PropTypes.func,
  resetLoads: PropTypes.func,
};

LoadTab.defaultProps = {
  loads: [],
  equipmentTypes: [],
  getEquipmentTypes: null,
  getLoads: null,
  setLoadsValues: null,
  updateLoads: null,
  resetLoads: null,
};

const mapStateToProps = state => ({
  equipmentTypes: state.manager.equipmentTypes,
  loads: state.manager.loads,
});

const mapDispatchToProps = {
  getEquipmentTypes,
  getLoads,
  setLoadsValues,
  updateLoads,
  resetLoads,
};

// Ver funcionamento do updateLoads para Apply
// Lembrar de enviar apenas os editados

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(LoadTab));
